import { useSnackbar } from 'notistack'
import { es, enUS } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { ChangeEvent, useEffect, useContext, useState } from 'react'
import { MenuItem, Radio, TextField, useMediaQuery, Select, Input, Checkbox, ListItemText, InputLabel } from '@mui/material'

import './styles/index.css'
import { config } from '../../config'
import Dialog from '../common/dialog'
import i18n from '../../utils/i18n.utils'
import { BankingPartner } from '../../types/user.types'
import { requestAccessToken } from '../../api/auth.api'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { getCustomerCompanies } from '../../api/customers.api'
import { parseNumber } from '../customer-companies-management/utils'
import { AppStateContext, AuthContext, UserContext } from '../../contexts'
import { CustomerCompany, FormError, Payment, ReportFilters } from '../../types'
import AutocompleteWithSelectAllMixin from '../common/autocomplete-with-select-all-mixin'
import { BalanceTransactionType, transactionStatusesData, bankingPartnersData, PaymentStatus, transactionTypesData, countriesCurrencies } from '../../lib'
import { getBankingPartnersData, createAndDownloadPaymentsReport, createAndDownloadPaymentsReportAsBatches } from '../../api/payments.api'

export const PaymentsHistoryModal = ({
  filtersFromOutside,
  customerCompanyPaymentsData,
  handleSetModalOpenState
}: {
  customerCompanyPaymentsData?: Array<Partial<Payment>>
  handleSetModalOpenState: (openState: boolean) => void
  filtersFromOutside?: { belongsTo: string; reportType?: string; selectedPocketId?: string }
}) => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const { closeSnackbar } = useSnackbar()
  const { customerCompanyId } = useParams()
  const { userSession } = useContext(UserContext)
  const { t } = useTranslation(['reports-management', 'common'])
  const mdDown = useMediaQuery((theme: any) => theme.breakpoints.down('md'))
  const { /* activateAuth, */ isAuth, removeAuth, updateSyncStatus } = useContext(AuthContext)
  const { banksData, handleShowCreatingReportNotification, handleStartTimer, defaultPocketIdSelected } = useContext(AppStateContext)

  const [batchId, setBatchId] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<FormError>({})
  const [selectedAmount, setSelectedAmount] = useState('')
  const [selectedPaxumId, setSelectedPaxumId] = useState('')
  const [selectedFileType, setSelectedFileType] = useState('pdf')
  const [batchOperationName, setBatchOperationName] = useState('')
  // const [selectedTransactionId, setSelectedTransactionId] = useState('')
  const [selectedDocumentNumber, setSelectedDocumentNumber] = useState('')
  const [toDateToFilter, setToDateToFilter] = useState<Date | null>(null)
  const [fromDateToFilter, setFromDateToFilter] = useState<Date | null>(null)
  const [selectedAccountNumberData, setSelectedAccountNumberData] = useState('')
  const [selectedSettlementDate, setSelectedSettlementDate] = useState('date-range')
  const [selectedCustomerCompany, setSelectedCustomerCompany] = useState<string>('')
  const [, /* selectedIdDocumentTypes */ setSelectedIdDocumentTypes] = useState<Array<string>>([])
  const [selectedBanks, setSelectedBanks] = useState<Array<{ value: string; label: string }>>([])
  const [allBanksOptions, setAllBanksOptions] = useState<Array<{ value: string; label: string }>>([])
  const [selectedExportTo, setSelectedExportTo] = useState<Array<{ bdValue: string; name: string }>>([])
  const [bankingPartnersList, setBankingPartnersList] = useState<Array<{ value: string; label: string }>>([])
  const [customerCompaniesList, setCustomerCompaniesList] = useState<Array<{ value: string; label: string }>>([])
  const [selectedBankingPartners, setSelectedBankingPartners] = useState<Array<{ value: string; label: string }>>([])
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState<Array<{ value?: string; label?: string } | null>>([])
  const [selectedTransactionStatus, setSelectedTransactionStatus] = useState<Array<{ value: PaymentStatus; name: string }>>([])
  const [selectedTransactionType, setSelectedTransactionType] = useState<Array<{ value: BalanceTransactionType; name: string }>>([])
  const [customerCompanyBeneficiariesList, setCustomerCompanyBeneficiariesList] = useState<Array<{ value: string; label: string }>>([])

  useEffect(() => {
    if (Array.isArray(customerCompanyPaymentsData) && customerCompanyPaymentsData.length > 0) {
      setIsLoading(true)
      setTimeout(() => handleGetCustomerCompanyBeneficiaries(), 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCompanyPaymentsData])

  useEffect(() => {
    if (filtersFromOutside?.reportType === 'batches') {
      setIsLoading(true)
      setSelectedFileType('csv')
      setSelectedSettlementDate('')
      setTimeout(() => handleGetComponentData(), 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (banksData) {
      const banksDataToDisplay = []

      for (const element of banksData) {
        const bankData = element

        banksDataToDisplay.push({
          value: bankData.achCode,
          label: bankData.name.toUpperCase()
        })
      }

      setAllBanksOptions(banksDataToDisplay)
    }
  }, [banksData])

  const handleGetComponentData = async () => {
    try {
      const {
        data: { accessToken /* refreshToken */ }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      // activateAuth?.({ token: refreshToken })

      await handleGetBankingPartners(accessToken)
      await handleGetCustomerCompanies(accessToken)
      setIsLoading(false)
    } catch (error: any) {
      console.log('Error handleGetComponentData', error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleGetBankingPartners = async (accessToken: string) => {
    try {
      const { data } = await getBankingPartnersData(accessToken)
      const bankingPartnersListToSet = data.map((bankingPartnerData: BankingPartner) => {
        if (bankingPartnerData.bdValue === 'cobre-pexto-v3') {
          return {
            label: 'COBRE v3',
            value: String(bankingPartnerData._id)
          }
        }

        if (bankingPartnerData.bdValue === 'cobre-pexto') {
          return {
            label: 'COBRE v2',
            value: String(bankingPartnerData._id)
          }
        }

        return {
          value: String(bankingPartnerData._id),
          label: bankingPartnerData.name.toUpperCase()
        }
      }) as Array<{ value: string; label: string }>

      setBankingPartnersList(bankingPartnersListToSet.sort((a, b) => a.label.localeCompare(b.label)))
      return true
    } catch (error: any) {
      console.log('Error handleGetBankingPartners', error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleGetCustomerCompanies = async (accessToken: string) => {
    try {
      const {
        data: { customerCompaniesData }
      } = await getCustomerCompanies(accessToken)
      const customerCompaniesListToSet = customerCompaniesData.map((customerCompanyData: CustomerCompany) => {
        return {
          value: String(customerCompanyData._id),
          label: customerCompanyData.businessCompany.name.toUpperCase()
        }
      }) as Array<{ value: string; label: string }>

      setCustomerCompaniesList(customerCompaniesListToSet.sort((a, b) => a.label.localeCompare(b.label)))
      return true
    } catch (error: any) {
      console.log('Error handleGetCustomerCompanies', error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleGetCustomerCompanyBeneficiaries = async () => {
    if (!customerCompanyPaymentsData) return

    const customerCompanyPaymentsDataUniqueByCustomerId = handleRemoveDuplicates(customerCompanyPaymentsData, 'customerId')
    const customerCompanyPaymentsDataUniqueByCustomerName = handleRemoveDuplicates(customerCompanyPaymentsDataUniqueByCustomerId, 'customerRegisteredFullName')

    const customerCompanyBeneficiariesListToSet = customerCompanyPaymentsDataUniqueByCustomerName.map((companyPaymentsData) => {
      return {
        value: companyPaymentsData.customerId,
        label: companyPaymentsData.customerRegisteredFullName ? companyPaymentsData.customerRegisteredFullName.toUpperCase() : companyPaymentsData.beneficiaryName.toUpperCase()
      }
    })

    setCustomerCompanyBeneficiariesList(customerCompanyBeneficiariesListToSet.sort((a, b) => a.label.localeCompare(b.label)))
    setIsLoading(false)
  }

  const handleRemoveDuplicates = (array: Array<any>, attribute: string) => {
    const seen = new Set()
    return array.filter((item) => {
      const key = item[attribute]
      if (seen.has(key)) {
        return false
      }
      seen.add(key)
      return true
    })
  }

  const handleDownloadRequestedReportAsBatches = async () => {
    setErrors({})
    let formDataIsValid = true
    const formErrors: FormError = {}

    const reportFilters = {
      batchId,
      toDateToFilter,
      fromDateToFilter,
      batchOperationName,
      selectedSettlementDate,
      selectedBankingPartners,
      selectedCustomerCompany
    }

    if (selectedSettlementDate === 'date-range' && !fromDateToFilter) {
      formDataIsValid = false
      formErrors.fromDateToFilter = t('modals.paymentsHistory.errors.fromDateToFilter')
    }

    if (selectedSettlementDate === 'date-range' && !toDateToFilter) {
      formDataIsValid = false
      formErrors.toDateToFilter = t('modals.paymentsHistory.errors.toDateToFilter')
    }

    if (selectedSettlementDate === 'date-range' && fromDateToFilter && toDateToFilter && toDateToFilter?.getTime() < fromDateToFilter?.getTime()) {
      formDataIsValid = false
      formErrors.toDateToFilter = t('modals.paymentsHistory.errors.toDateToFilterEarlier')
    }

    if (!selectedCustomerCompany) {
      formDataIsValid = false
      formErrors.selectedCustomerCompany = t('modals.paymentsHistory.errors.selectedCustomerCompany')
    }

    if (!formDataIsValid) {
      setErrors(formErrors)
      return
    }

    const notificationKey = handleShowCreatingReportNotification?.()

    try {
      setIsLoading(true)
      const {
        data: { accessToken /* refreshToken */ }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      updateSyncStatus?.(true)
      // activateAuth?.({ token: refreshToken })

      const paymentsHistoryReportDocumentToDownload = await createAndDownloadPaymentsReportAsBatches(
        {
          reportFilters,
          notificationKey,
          reportLanguage: i18n.language,
          role: String(userSession?.role),
          userSessionId: String(userSession?._id)
        },
        accessToken
      )

      setIsLoading(false)

      if (paymentsHistoryReportDocumentToDownload.data === 'Empty report') {
        closeSnackbar(notificationKey)
        setErrors({ generalError: t('formGeneralErrors.emptyReport', { ns: 'common' }) })
        return
      }

      handleStartTimer?.()
      handleSetModalOpenState(false)
    } catch (error: any) {
      console.log('Error handleDownloadRequestedReport', error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401 && error?.response?.data.message === 'Not authorized: Exists') {
        setErrors({ generalError: `${t('formGeneralErrors.unauthorized', { ns: 'common' })}: 406` })
      } else if (error?.response?.status === 401 && !(error?.response?.data.message === 'Not authorized: Exists')) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 504) {
        setErrors({ generalError: t('formGeneralErrors.reportSentToEmail', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      closeSnackbar(notificationKey)
      setIsLoading(false)
    }
  }

  const handleGetBelongsTo = () => {
    if (userSession?.belongsTo === 'holdings') {
      return customerCompanyId
    }

    if (userSession?.belongsTo !== 'holdings' && filtersFromOutside?.belongsTo) {
      return String(filtersFromOutside.belongsTo)
    }

    return String(userSession?.belongsTo)
  }

  const handleDownloadRequestedReport = async () => {
    setErrors({})
    let formDataIsValid = true
    const formErrors: FormError = {}
    const reportFilters = {
      selectedBanks,
      toDateToFilter,
      selectedPaxumId,
      fromDateToFilter,
      selectedExportTo,
      selectedFileType,
      // selectedTransactionId,
      selectedBeneficiaries,
      selectedDocumentNumber,
      selectedSettlementDate,
      // selectedIdDocumentTypes,
      selectedTransactionType,
      selectedTransactionStatus,
      selectedAccountNumberData,
      selectedAmount: selectedAmount.replaceAll('$', '').replaceAll(',', '').trim()
    } as ReportFilters

    if (selectedSettlementDate === 'date-range' && !fromDateToFilter) {
      formDataIsValid = false
      formErrors.fromDateToFilter = t('modals.paymentsHistory.errors.fromDateToFilter')
    }

    if (selectedSettlementDate === 'date-range' && !toDateToFilter) {
      formDataIsValid = false
      formErrors.toDateToFilter = t('modals.paymentsHistory.errors.toDateToFilter')
    }

    if (selectedSettlementDate === 'date-range' && fromDateToFilter && toDateToFilter && toDateToFilter?.getTime() < fromDateToFilter?.getTime()) {
      formDataIsValid = false
      formErrors.toDateToFilter = t('modals.paymentsHistory.errors.toDateToFilterEarlier')
    }

    if (!formDataIsValid) {
      setErrors(formErrors)
      return
    }

    const notificationKey = handleShowCreatingReportNotification?.()

    try {
      setIsLoading(true)
      const {
        data: { accessToken /* refreshToken */ }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      updateSyncStatus?.(true)
      // activateAuth?.({ token: refreshToken })

      const paymentsHistoryReportDocumentToDownload = await createAndDownloadPaymentsReport(
        {
          currency,
          reportFilters,
          notificationKey,
          reportLanguage: i18n.language,
          role: String(userSession?.role),
          userSessionId: String(userSession?._id),
          pocketToReport:
            filtersFromOutside && filtersFromOutside.selectedPocketId
              ? String(filtersFromOutside && filtersFromOutside.selectedPocketId)
              : !filtersFromOutside && String(userSession?.role) === 'admin-client'
                ? defaultPocketIdSelected
                : '',
          belongsTo: handleGetBelongsTo()
        },
        accessToken
      )

      setIsLoading(false)

      if (paymentsHistoryReportDocumentToDownload.data === 'Empty report') {
        closeSnackbar(notificationKey)
        setErrors({ generalError: t('formGeneralErrors.emptyReport', { ns: 'common' }) })
        return
      }

      handleStartTimer?.()
      handleSetModalOpenState(false)
    } catch (error: any) {
      console.log('Error handleDownloadRequestedReport', error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401 && error?.response?.data.message === 'Not authorized: Exists') {
        setErrors({ generalError: `${t('formGeneralErrors.unauthorized', { ns: 'common' })}: 406` })
      } else if (error?.response?.status === 401 && !(error?.response?.data.message === 'Not authorized: Exists')) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 504) {
        setErrors({ generalError: t('formGeneralErrors.reportSentToEmail', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      closeSnackbar(notificationKey)
      setIsLoading(false)
    }
  }

  const handleGetNumberAsCurrencyValue = (valueToSet: string) => {
    const extractedNumberOfValueToSet = valueToSet.replaceAll('$', '').replaceAll(',', '').replaceAll('.', '').trim()

    if (isNaN(Number(extractedNumberOfValueToSet))) return ''

    if (extractedNumberOfValueToSet === '') return ''

    return parseNumber(extractedNumberOfValueToSet)
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <Dialog
      errors={errors}
      maxHeight="865px"
      closeOptionWarning={true}
      dialogIsLoading={isLoading}
      minWidth={mdDown ? '0px' : '800px'}
      dialogTitle={t('modals.paymentsHistory.title')}
      handleDialogAction={() => {
        if (filtersFromOutside?.reportType && filtersFromOutside?.reportType === 'batches') {
          handleDownloadRequestedReportAsBatches()
          return
        }

        handleDownloadRequestedReport()
      }}
      handleCloseOptionAction={() => handleSetModalOpenState(false)}
      actionButtonText={t('modals.paymentsHistory.buttons.download')}
      handleDialogOpenState={(openState: boolean) => handleSetModalOpenState(openState)}
    >
      <div className="payments-history-modal-container">
        {(!filtersFromOutside?.reportType || (filtersFromOutside?.reportType && filtersFromOutside?.reportType !== 'batches')) && (
          <div className="payments-history-modal-full-column-container">
            <div className="payments-history-modal-options-column">
              <div className="payments-history-modal-subtitle">{t('modals.paymentsHistory.subtitles.fileType')}</div>
              <div className="payments-history-modal-options-row">
                <div
                  onClick={() => {
                    setErrors({})

                    setSelectedBanks([])
                    setSelectedAmount('')
                    setSelectedPaxumId('')
                    setSelectedExportTo([])
                    // setSelectedTransactionId('')
                    setSelectedBeneficiaries([])
                    setSelectedDocumentNumber('')
                    setSelectedTransactionType([])
                    setSelectedIdDocumentTypes([])
                    setSelectedTransactionStatus([])
                    setSelectedAccountNumberData('')

                    setSelectedFileType('pdf')
                    setToDateToFilter(null)
                    setFromDateToFilter(null)
                    setSelectedSettlementDate('date-range')
                  }}
                  style={{ cursor: 'pointer' }}
                  className="payments-history-modal-full-column-container"
                >
                  <Radio
                    size="small"
                    checked={selectedFileType === 'pdf'}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setSelectedFileType(event.target.value)}
                    value="pdf"
                    name="radio-buttons"
                    inputProps={{ 'aria-label': 'PDF' }}
                    sx={{
                      '&.Mui-checked': {
                        color: '#2ed3b7'
                      }
                    }}
                  />
                  <div>PDF</div>
                </div>
                <div
                  onClick={() => {
                    setErrors({})
                    setSelectedFileType('csv')
                    setToDateToFilter(null)
                    setFromDateToFilter(null)
                    setSelectedSettlementDate('')
                  }}
                  style={{ cursor: 'pointer' }}
                  className="payments-history-modal-full-column-container"
                >
                  <Radio
                    size="small"
                    checked={selectedFileType === 'csv'}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setSelectedFileType(event.target.value)}
                    value="csv"
                    name="radio-buttons"
                    inputProps={{ 'aria-label': 'CSV' }}
                    sx={{
                      '&.Mui-checked': {
                        color: '#2ed3b7'
                      }
                    }}
                  />
                  <div>CSV</div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="payments-history-modal-full-column-container">
          <div className="payments-history-modal-options-column">
            <div className="payments-history-modal-subtitle">{t('modals.paymentsHistory.subtitles.settlementDate')}</div>
            <div className="payments-history-modal-options-row">
              {selectedFileType === 'csv' && (
                <>
                  <div
                    onClick={() => {
                      setErrors({})
                      setToDateToFilter(null)
                      setFromDateToFilter(null)

                      if (selectedSettlementDate === 'current-hour') {
                        setSelectedSettlementDate('')
                      } else {
                        setSelectedSettlementDate('current-hour')
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                    className="payments-history-modal-full-column-container"
                  >
                    <Radio
                      size="small"
                      checked={selectedSettlementDate === 'current-hour'}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => setSelectedSettlementDate(event.target.value)}
                      value="current-hour"
                      name="radio-buttons"
                      inputProps={{ 'aria-label': 'current-hour' }}
                      sx={{
                        '&.Mui-checked': {
                          color: '#2ed3b7'
                        }
                      }}
                    />
                    <div>{t('modals.paymentsHistory.options.currentHour')}</div>
                  </div>
                  <div
                    onClick={() => {
                      setErrors({})
                      setToDateToFilter(null)
                      setFromDateToFilter(null)

                      if (selectedSettlementDate === 'last-hour') {
                        setSelectedSettlementDate('')
                      } else {
                        setSelectedSettlementDate('last-hour')
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                    className="payments-history-modal-full-column-container"
                  >
                    <Radio
                      size="small"
                      checked={selectedSettlementDate === 'last-hour'}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => setSelectedSettlementDate(event.target.value)}
                      value="last-hour"
                      name="radio-buttons"
                      inputProps={{ 'aria-label': 'last-hour' }}
                      sx={{
                        '&.Mui-checked': {
                          color: '#2ed3b7'
                        }
                      }}
                    />
                    <div>{t('modals.paymentsHistory.options.lastHour')}</div>
                  </div>
                </>
              )}
              <div
                onClick={() => {
                  setErrors({})
                  setToDateToFilter(null)
                  setFromDateToFilter(null)

                  if (selectedSettlementDate === 'date-range' && selectedFileType === 'csv') {
                    setSelectedSettlementDate('')
                  } else {
                    setSelectedSettlementDate('date-range')
                  }
                }}
                style={{ cursor: 'pointer' }}
                className="payments-history-modal-full-column-container"
              >
                <Radio
                  size="small"
                  checked={selectedSettlementDate === 'date-range'}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => setSelectedSettlementDate(event.target.value)}
                  value="date-range"
                  name="radio-buttons"
                  inputProps={{ 'aria-label': 'date-range' }}
                  sx={{
                    '&.Mui-checked': {
                      color: '#2ed3b7'
                    }
                  }}
                />
                <div>{t('modals.paymentsHistory.options.dateRange')}</div>
              </div>
            </div>
          </div>
        </div>
        {selectedSettlementDate === 'date-range' && (
          <div
            style={{ alignItems: 'flex-end', gap: '20px' }}
            className="payments-history-modal-full-column-container"
          >
            <div className="payments-history-modal-options-column">
              <div className="payments-history-modal-subtitle">{t('modals.paymentsHistory.subtitles.selectSettlementDateRange')}</div>
              <div>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={i18n && i18n.language.indexOf('es') !== -1 ? es : enUS}
                >
                  <DatePicker
                    label={t('modals.paymentsHistory.options.fromDate')}
                    sx={{ width: '100%' }}
                    onChange={(newValue) => setFromDateToFilter(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'standard',
                        error: !!errors.fromDateToFilter,
                        helperText: errors.fromDateToFilter,
                        sx: { ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }
                      }
                    }}
                  />
                </LocalizationProvider>
              </div>
            </div>
            <div className="payments-history-modal-options-column">
              <div>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={i18n && i18n.language.indexOf('es') !== -1 ? es : enUS}
                >
                  <DatePicker
                    label={t('modals.paymentsHistory.options.toDate')}
                    sx={{ width: '100%' }}
                    onChange={(newValue) => setToDateToFilter(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'standard',
                        error: !!errors.toDateToFilter,
                        helperText: errors.toDateToFilter,
                        sx: { ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }
                      }
                    }}
                  />
                </LocalizationProvider>
              </div>
            </div>
          </div>
        )}
        {selectedFileType === 'csv' && (!filtersFromOutside?.reportType || (filtersFromOutside?.reportType && filtersFromOutside?.reportType !== 'batches')) && (
          <div className="payments-history-modal-filters-container">
            <div className="row-container">
              <div className="text-field-container">
                <div style={selectedTransactionType.length > 0 ? { width: '100%', marginTop: '0px' } : { width: '100%', marginTop: '16px' }}>
                  {selectedTransactionType.length > 0 && (
                    <InputLabel style={{ fontSize: '0.8rem', padding: '0px 16px', boxSizing: 'border-box', color: 'rgb(46, 211, 183)' }}>{t('modals.paymentsHistory.options.transactionType')}</InputLabel>
                  )}
                  <Select
                    multiple
                    fullWidth
                    value={selectedTransactionType}
                    onChange={(event) => {
                      const selectedValues = [...event.target.value] as Array<{ value: BalanceTransactionType; name: string }>

                      if (selectedValues.length === 1) {
                        setSelectedTransactionType(selectedValues)
                        return
                      }

                      const lastSelectedvalue = selectedValues[selectedValues.length - 1]
                      const previousSelectedvalues = selectedValues.slice(0, -1)

                      if (previousSelectedvalues.length === 0) {
                        setSelectedTransactionType(previousSelectedvalues)
                        return
                      }

                      if (previousSelectedvalues?.findIndex((selectedValue: { value: BalanceTransactionType; name: string }) => selectedValue.value === lastSelectedvalue.value) === -1) {
                        setSelectedTransactionType(selectedValues)
                      } else {
                        const valuesToSetUpdated = selectedValues.filter((selectedValue: { value: BalanceTransactionType; name: string }) => selectedValue.value !== lastSelectedvalue.value)
                        setSelectedTransactionType(valuesToSetUpdated)
                      }
                    }}
                    renderValue={(selected) => selected.map((x) => x.name.toUpperCase()).join(', ')}
                    input={
                      <Input
                        sx={{
                          ...textFieldWhiteBg,
                          '& .MuiSelect-select': { lineHeight: '250%', color: 'rgb(46, 211, 183)' },
                          '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' },
                          '&::before': { borderBottom: '1px solid rgba(226, 242, 246, 0.42)' },
                          '&::after': { borderBottom: '2px solid rgb(46, 211, 183)' },
                          '& .MuiSelect-select span::before': {
                            content: `"${t('modals.paymentsHistory.options.transactionType')}"`,
                            color: 'rgb(46, 211, 183)'
                          },
                          '&:hover:not(.Mui-disabled, .Mui-error):before': { borderBottom: '2px solid rgb(46, 211, 183)' }
                        }}
                      />
                    }
                  >
                    {transactionTypesData.map((transactionTypeData: { value: BalanceTransactionType; name: string }, index: number) => (
                      <MenuItem
                        key={index}
                        value={transactionTypeData as unknown as string}
                      >
                        <Checkbox
                          sx={{
                            '& .MuiSvgIcon-root': { fill: '#15B79E' }
                          }}
                          checked={selectedTransactionType.findIndex((item) => item.value === transactionTypeData.value) >= 0}
                        />
                        <ListItemText primary={transactionTypeData.name.toUpperCase()} />
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="text-field-container">
                <TextField
                  fullWidth
                  variant="standard"
                  label={t('modals.paymentsHistory.options.beneficiaryId')}
                  value={selectedDocumentNumber ? selectedDocumentNumber : ''}
                  onChange={(event) => setSelectedDocumentNumber(event.target.value)}
                  sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
                />
                {/* <TextField
                  fullWidth
                  variant="standard"
                  label={t('modals.paymentsHistory.options.transactionId')}
                  value={selectedTransactionId ? selectedTransactionId : ''}
                  onChange={(event) => setSelectedTransactionId(event.target.value)}
                  sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
                /> */}
                {/* <div style={selectedIdDocumentTypes.length > 0 ? { width: '100%', marginTop: '0px' } : { width: '100%', marginTop: '16px' }}>
                  {selectedIdDocumentTypes.length > 0 && (
                    <InputLabel style={{ fontSize: '0.8rem', padding: '0px 16px', boxSizing: 'border-box', color: 'rgb(46, 211, 183)' }}>{t('modals.paymentsHistory.options.idDocumentType')}</InputLabel>
                  )}
                  <Select
                    multiple
                    fullWidth
                    value={selectedIdDocumentTypes}
                    onChange={(event) => {
                      const selectedValues = [...event.target.value] as Array<string>

                      if (selectedValues.length === 1) {
                        setSelectedIdDocumentTypes(selectedValues)
                        return
                      }

                      const lastSelectedvalue = selectedValues[selectedValues.length - 1]
                      const previousSelectedvalues = selectedValues.slice(0, -1)

                      if (previousSelectedvalues.length === 0) {
                        setSelectedIdDocumentTypes(previousSelectedvalues)
                        return
                      }

                      if (previousSelectedvalues?.findIndex((selectedValue: string) => selectedValue === lastSelectedvalue) === -1) {
                        setSelectedIdDocumentTypes(selectedValues)
                      } else {
                        const valuesToSetUpdated = selectedValues.filter((selectedValue: string) => selectedValue !== lastSelectedvalue)
                        setSelectedIdDocumentTypes(valuesToSetUpdated)
                      }
                    }}
                    renderValue={(selected) => selected.map((x) => x.toUpperCase()).join(', ')}
                    input={
                      <Input
                        sx={{
                          ...textFieldWhiteBg,
                          '& .MuiSelect-select': { lineHeight: '250%', color: 'rgb(46, 211, 183)' },
                          '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' },
                          '&::before': { borderBottom: '1px solid rgba(226, 242, 246, 0.42)' },
                          '&::after': { borderBottom: '2px solid rgb(46, 211, 183)' },
                          '& .MuiSelect-select span::before': {
                            content: `"${t('modals.paymentsHistory.options.idDocumentType')}"`,
                            color: 'rgb(46, 211, 183)'
                          },
                          '&:hover:not(.Mui-disabled, .Mui-error):before': { borderBottom: '2px solid rgb(46, 211, 183)' }
                        }}
                      />
                    }
                  >
                    {Object.keys(customersDocumentType).map((documentType: string, index: number) => (
                      <MenuItem
                        key={index}
                        value={documentType as string}
                      >
                        <Checkbox
                          sx={{
                            '& .MuiSvgIcon-root': { fill: '#15B79E' }
                          }}
                          checked={selectedIdDocumentTypes.findIndex((item) => item === documentType) >= 0}
                        />
                        <ListItemText primary={(customersDocumentType as any)[documentType].toUpperCase()} />
                      </MenuItem>
                    ))}
                  </Select>
                </div> */}
              </div>
            </div>
            <div className="row-container">
              {((config.environment !== 'production' && (String(userSession?.belongsTo) === String(config.app.demoPaxumCompanyId) || String(userSession?.belongsTo) === String(config.app.paxumCompanyId))) ||
                (config.environment === 'production' && String(userSession?.belongsTo) === String(config.app.paxumCompanyId))) && (
                <div className="text-field-container">
                  <TextField
                    fullWidth
                    variant="standard"
                    label={t('modals.paymentsHistory.options.paxumId')}
                    value={selectedPaxumId ? selectedPaxumId : ''}
                    onChange={(event) => setSelectedPaxumId(event.target.value)}
                    sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
                  />
                </div>
              )}
              <div className="text-field-container">
                <div style={selectedTransactionStatus.length > 0 ? { width: '100%', marginTop: '0px' } : { width: '100%', marginTop: '16px' }}>
                  {selectedTransactionStatus.length > 0 && (
                    <InputLabel style={{ fontSize: '0.8rem', padding: '0px 16px', boxSizing: 'border-box', color: 'rgb(46, 211, 183)' }}>{t('modals.paymentsHistory.options.status')}</InputLabel>
                  )}
                  <Select
                    multiple
                    fullWidth
                    value={selectedTransactionStatus}
                    onChange={(event) => {
                      const selectedValues = [...event.target.value] as Array<{ value: PaymentStatus; name: string }>

                      if (selectedValues.length === 1) {
                        setSelectedTransactionStatus(selectedValues)
                        return
                      }

                      const lastSelectedvalue = selectedValues[selectedValues.length - 1]
                      const previousSelectedvalues = selectedValues.slice(0, -1)

                      if (previousSelectedvalues.length === 0) {
                        setSelectedTransactionStatus(previousSelectedvalues)
                        return
                      }

                      if (previousSelectedvalues?.findIndex((selectedValue: { value: PaymentStatus; name: string }) => selectedValue.value === lastSelectedvalue.value) === -1) {
                        setSelectedTransactionStatus(selectedValues)
                      } else {
                        const valuesToSetUpdated = selectedValues.filter((selectedValue: { value: PaymentStatus; name: string }) => selectedValue.value !== lastSelectedvalue.value)
                        setSelectedTransactionStatus(valuesToSetUpdated)
                      }
                    }}
                    renderValue={(selected) => selected.map((x) => x.name.toUpperCase()).join(', ')}
                    input={
                      <Input
                        sx={{
                          ...textFieldWhiteBg,
                          '& .MuiSelect-select': { lineHeight: '250%', color: 'rgb(46, 211, 183)' },
                          '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' },
                          '&::before': { borderBottom: '1px solid rgba(226, 242, 246, 0.42)' },
                          '&::after': { borderBottom: '2px solid rgb(46, 211, 183)' },
                          '& .MuiSelect-select span::before': {
                            content: `"${t('modals.paymentsHistory.options.status')}"`,
                            color: 'rgb(46, 211, 183)'
                          },
                          '&:hover:not(.Mui-disabled, .Mui-error):before': { borderBottom: '2px solid rgb(46, 211, 183)' }
                        }}
                      />
                    }
                  >
                    {transactionStatusesData.map((transactionStatusData: { value: PaymentStatus; name: string }, index: number) => (
                      <MenuItem
                        key={index}
                        value={transactionStatusData as unknown as string}
                      >
                        <Checkbox
                          sx={{
                            '& .MuiSvgIcon-root': { fill: '#15B79E' }
                          }}
                          checked={selectedTransactionStatus.findIndex((item) => item.value === transactionStatusData.value) >= 0}
                        />
                        <ListItemText primary={transactionStatusData.name.toUpperCase()} />
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
            <div className="row-container">
              {((config.environment !== 'production' && (String(userSession?.belongsTo) === String(config.app.demoPaxumCompanyId) || String(userSession?.belongsTo) === String(config.app.paxumCompanyId))) ||
                (config.environment === 'production' && String(userSession?.belongsTo) === String(config.app.paxumCompanyId))) && (
                <div className="text-field-container">
                  <div style={selectedExportTo.length > 0 ? { width: '100%', marginTop: '0px' } : { width: '100%', marginTop: '16px' }}>
                    {selectedExportTo.length > 0 && (
                      <InputLabel style={{ fontSize: '0.8rem', padding: '0px 16px', boxSizing: 'border-box', color: 'rgb(46, 211, 183)' }}>{t('modals.paymentsHistory.options.exportTo')}</InputLabel>
                    )}
                    <Select
                      multiple
                      fullWidth
                      value={selectedExportTo}
                      onChange={(event) => {
                        const selectedvalue = [...event.target.value] as Array<{ bdValue: string; name: string }>
                        setSelectedExportTo(selectedvalue)
                      }}
                      renderValue={(selected) => selected.map((x) => x.name).join(', ')}
                      input={
                        <Input
                          sx={{
                            ...textFieldWhiteBg,
                            '& .MuiSelect-select': { lineHeight: '250%', color: 'rgb(46, 211, 183)' },
                            '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' },
                            '&::before': { borderBottom: '1px solid rgba(226, 242, 246, 0.42)' },
                            '&::after': { borderBottom: '2px solid rgb(46, 211, 183)' },
                            '& .MuiSelect-select span::before': {
                              content: `"${t('modals.paymentsHistory.options.exportTo')}"`,
                              color: 'rgb(46, 211, 183)'
                            },
                            '&:hover:not(.Mui-disabled, .Mui-error):before': { borderBottom: '2px solid rgb(46, 211, 183)' }
                          }}
                        />
                      }
                    >
                      {bankingPartnersData.map((bankingPartnerData: { bdValue: string; name: string }, index: number) => (
                        <MenuItem
                          key={index}
                          value={bankingPartnerData as unknown as string}
                        >
                          <Checkbox
                            sx={{
                              '& .MuiSvgIcon-root': { fill: '#15B79E' }
                            }}
                            checked={selectedExportTo.findIndex((item) => item.bdValue === bankingPartnerData.bdValue) >= 0}
                          />
                          <ListItemText primary={bankingPartnerData.name.toUpperCase()} />
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>
              )}
              <div className="text-field-container">
                <AutocompleteWithSelectAllMixin
                  id="autocomplete-with-select-all"
                  multiple
                  fullWidth
                  options={customerCompanyBeneficiariesList}
                  setValue={setSelectedBeneficiaries}
                  disableCloseOnSelect
                  filterSelectedOptions
                  freeSolo={false}
                  value={selectedBeneficiaries}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label={t('modals.paymentsHistory.options.beneficiaryName')}
                      sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
                    />
                  )}
                />
              </div>
            </div>
            <div className="row-container">
              <div className="text-field-container">
                <AutocompleteWithSelectAllMixin
                  id="autocomplete-with-select-all"
                  multiple
                  fullWidth
                  options={allBanksOptions}
                  setValue={setSelectedBanks}
                  disableCloseOnSelect
                  filterSelectedOptions
                  freeSolo={false}
                  value={selectedBanks}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label={t('modals.paymentsHistory.options.bank')}
                      sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
                    />
                  )}
                />
              </div>
              <div className="text-field-container">
                <TextField
                  fullWidth
                  variant="standard"
                  label={t('modals.paymentsHistory.options.accountNumber')}
                  value={selectedAccountNumberData || ''}
                  onChange={(event) => setSelectedAccountNumberData(event.target.value)}
                  sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
                />
              </div>
            </div>
            <div className="row-container">
              <div className="text-field-container">
                <TextField
                  fullWidth
                  variant="standard"
                  label={t('modals.paymentsHistory.options.amount')}
                  value={selectedAmount ? selectedAmount : ''}
                  onChange={(event) => {
                    const amountToSet = handleGetNumberAsCurrencyValue(String(event.target.value))

                    if (amountToSet === '') {
                      setSelectedAmount(amountToSet)
                      return
                    }

                    setSelectedAmount(`$ ${amountToSet}`)
                  }}
                  sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
                />
              </div>
            </div>
          </div>
        )}
        {selectedFileType === 'csv' && filtersFromOutside?.reportType && filtersFromOutside?.reportType === 'batches' && (
          <div className="payments-history-modal-filters-container">
            <div className="row-container">
              <div className="text-field-container">
                <TextField
                  select
                  fullWidth
                  variant="standard"
                  value={selectedCustomerCompany || ''}
                  error={!!errors.selectedCustomerCompany}
                  helperText={errors.selectedCustomerCompany}
                  label={t('modals.paymentsHistory.options.client')}
                  onChange={(event) => setSelectedCustomerCompany(event.target.value)}
                  sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
                >
                  <MenuItem
                    disabled
                    value=""
                  >
                    {t('modals.paymentsHistory.options.select')}
                  </MenuItem>
                  {customerCompaniesList.map((customerCompanyItem: { value: string; label: string }, index: number) => {
                    return (
                      <MenuItem
                        key={index}
                        value={String(customerCompanyItem.value)}
                      >
                        {customerCompanyItem.label?.toUpperCase()}
                      </MenuItem>
                    )
                  })}
                </TextField>
              </div>
              <div className="text-field-container">
                <AutocompleteWithSelectAllMixin
                  id="autocomplete-with-select-all"
                  multiple
                  fullWidth
                  options={bankingPartnersList}
                  setValue={setSelectedBankingPartners}
                  disableCloseOnSelect
                  filterSelectedOptions
                  freeSolo={false}
                  value={selectedBankingPartners}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label={t('modals.paymentsHistory.options.bankingPartner')}
                      sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
                    />
                  )}
                />
              </div>
            </div>
            <div className="row-container">
              <div className="text-field-container">
                <TextField
                  fullWidth
                  variant="standard"
                  value={batchId || ''}
                  label={t('modals.paymentsHistory.options.batchId')}
                  onChange={(event) => setBatchId(String(event.target.value))}
                  sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
                />
              </div>
              <div className="text-field-container">
                <TextField
                  fullWidth
                  variant="standard"
                  value={batchOperationName || ''}
                  label={t('modals.paymentsHistory.options.batchOperationName')}
                  onChange={(event) => setBatchOperationName(String(event.target.value).toUpperCase())}
                  sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  )
}
